import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Input,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

// Models
import { ApiResponse } from '../shared/models/api-response.model';
import { ErrorTriggers } from './models/error-triggers.model';
import { PaymentPlanPreview } from './models/payment-plan-preview.model';
import { PaymentTerm } from './models/payment-term.model';
import { PaymentTermForm } from './models/payment-term-form.model';
import { SaleGroupItem } from './models/sale-group-items.model';

// Services
import { PaymentTermsService } from './payment-terms.service';
import { AmountFormatterService } from 'src/sale-item/amount-formatter.service';

// components
import { UpfrontPaymentAmountComponent } from './upfront-payment-amount/upfront-payment-amount.component';
import { SeFeThemesLoader } from 'se-fe-themes';
import { SeFeFormFieldConfig } from 'se-fe-form-field';

@Component({
  selector: 'payment-terms-selector-element',
  styleUrls: ['./payment-terms-selector.component.scss'],
  templateUrl: './payment-terms-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentTermsSelectorComponent implements OnChanges, OnInit, OnDestroy {
  public paymentTermFieldConfig: SeFeFormFieldConfig = {
    label: 'Payment terms',
    errorMessages: {
      required: 'Select payment terms',
    },
  };
  public initialAmount = '0.00';
  public paymentTerm: PaymentTerm;
  public paymentTermForm: FormGroup;
  public paymentTermsList: PaymentTerm[];
  public previewData: PaymentPlanPreview;
  public previewLoading: boolean;
  public saleGroupItems: SaleGroupItem[];
  public showUpfrontAmountForm = false;
  public loaded = false;
  public showPartialAmountInput = false;
  public itemized = false;
  public disabled = false;
  public applied: boolean;
  public isInvoice: boolean;
  public saleTypeForTranslation: string;
  customDatesSubscription: Subscription;
  upfrontPaymentAmountSubscription: Subscription;
  triggerErrorSubscription: Subscription;
  upfrontAmountAppliedSubscription: Subscription;

  @ViewChild(UpfrontPaymentAmountComponent) upfrontPaymentAmount: UpfrontPaymentAmountComponent;

  @Input() addPaymentTerms?: boolean;
  @Input() newTermsAdded?: boolean;
  @Input() organizationId: string;
  @Input() preSelectedCustomDates?: string;
  @Input() preSelectedTermId?: string;
  // tslint:disable-next-line:no-input-rename
  @Input('saleGroupItems') items?: string;
  @Input() saleType: string;
  @Input() totalAmount?: string;
  @Input() triggerPaymentTermError?: string;

  @Output() openPaymentTermsModal = new EventEmitter<Boolean>();
  @Output() outputPaymentTermForm = new EventEmitter<PaymentTermForm>();

  constructor(
    private amountFormatter: AmountFormatterService,
    private detector: ChangeDetectorRef,
    private elementRef: ElementRef,
    private formBuilder: FormBuilder,
    private paymentTermsService: PaymentTermsService,
    private translate: TranslateService
  ) {
    translate.setDefaultLang('en');
    translate.use('en');
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com'
    });
  }

  public ngOnInit(): void {
    const svgEvent = new Event('se-financial-admin-load-svg', { bubbles: true });
    this.elementRef.nativeElement.dispatchEvent(svgEvent);
    this.isInvoice = this.saleType === 'invoice';
    this.saleTypeForTranslation = this.isInvoice ? this.saleType : 'registration';
    this.subscribeToCustomDates();
    this.subscribeToUpfrontPaymentAmount();
    this.subscribeToTriggerError();
    this.outputPaymentTermForm.emit(new PaymentTermForm(this.paymentTermForm, this.paymentTerm, this.previewData));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.triggerPaymentTermError?.currentValue !== 'true') {
      this.showPartialAmountInput = false;
      this.initialAmount = '0.00';
      this.showUpfrontAmountForm = false;
      this.setSaleGroupItems();
      this.loadTerms();
    }
    this.setTriggerError();
  }

  public ngOnDestroy(): void {
    this.customDatesSubscription.unsubscribe();
    this.upfrontPaymentAmountSubscription.unsubscribe();
    this.triggerErrorSubscription.unsubscribe();
  }

  public get paymentTermSelectControl(): AbstractControl {
    if (this.paymentTermForm) {
      return this.paymentTermForm.get('paymentTermSelect') as FormControl;
    }
  }

  public selectTerm(): void {
    this.triggerPaymentTermError = '';
    this.setTriggerError();
    const paymentTermId = Number(this.paymentTermForm.getRawValue().paymentTermSelect);
    this.setSelectedTerm(paymentTermId);
    this.loadPreview(paymentTermId);
    if (this.upfrontPaymentAmount) { this.upfrontPaymentAmount.resetUpfrontAmount(); }
  }

  private loadPreview(paymentTermId: number): void {
    if (this.saleType === 'invoice' && paymentTermId) {
      this.previewLoading = true;
      const sanitizedInitialAmount = this.amountFormatter.sanitizePrice(this.initialAmount);
      this.paymentTermsService.getPlanPreview(paymentTermId, this.saleGroupItems, Number(sanitizedInitialAmount), this.totalAmount)
        .subscribe((preview: ApiResponse<PaymentPlanPreview>) => {
          this.previewData = preview.result;
          this.outputPaymentTermForm.emit(new PaymentTermForm(this.paymentTermForm, this.paymentTerm, this.previewData));
          this.previewLoading = false;
          this.detector.detectChanges();
        });
    }
  }

  public loadTerms(): void {
    this.paymentTermsService.getPaymentTerms(this.organizationId)
      .subscribe((terms: ApiResponse<PaymentTerm[]>) => {
        this.paymentTermsList = terms.result.map(term => new PaymentTerm(term));
        if (this.isInvoice) { this.setDueInFullTerms(); }
        this.setElement();
      });
  }

  public setElement(): void {
    this.loaded = false;
    this.setupForm();
    if (this.preSelectedTermId) {
      this.paymentTermForm.reset({ paymentTermSelect: Number(this.preSelectedTermId) });
      this.selectTerm();
    } else {
      this.paymentTermForm.reset();
      if (this.isInvoice) { this.paymentTerm = null; }
      this.outputPaymentTermForm.emit(new PaymentTermForm(this.paymentTermForm, this.paymentTerm, this.previewData));
    }
    this.loaded = true;
  }

  public setDueInFullTerms() {
    this.disabled = false;
    if (!this.newTermsAdded) { this.preSelectedTermId = null; }
    if (this.saleGroupItems && this.allDueInFull()) {
      this.disabled = true;
      this.preSelectedTermId = this.paymentTermsList.find((term: PaymentTerm) =>
        term.payment_term_strategy.strategy_type === 'due_in_full'
      ).id.toString();
    }
    this.newTermsAdded = false;
  }

  public toggleUpfrontPayment(): void {
    this.showPartialAmountInput = !this.showPartialAmountInput;
    if (!this.showPartialAmountInput && Number(this.previewData.payments[0].amount) !== 0) {
      this.initialAmount = '0.00';
      this.paymentTermForm.removeControl('upfrontAmount');
      this.loadPreview(Number(this.paymentTermForm.value.paymentTermSelect));
    }
  }

  public setSaleGroupItems(): void {
    if (!this.items) {
      this.saleGroupItems = null;
      return;
    }
    try {
      this.saleGroupItems = JSON.parse(this.items);
    } catch (ex) {
      this.saleGroupItems = null;
      console.error('Error parsing sale group items', this.items, ex);
    }
    if (typeof this.saleGroupItems !== 'object' || this.saleGroupItems === null) {
      console.error('Parsed sale group items is not a valid JSON object', this.items);
      this.saleGroupItems = null;
    }
  }

  private setTriggerError(): void {
    let triggerError = false;
    if (this.triggerPaymentTermError === 'true') { triggerError = true; }
    this.paymentTermsService.updateErrorTriggers({ triggerError: triggerError, emitForm: false });
  }

  private allDueInFull(): boolean {
    if (this.saleGroupItems && this.saleGroupItems.length) {
      return this.saleGroupItems.every((item: SaleGroupItem) => item.price_cents === item.upfront_amount_cents);
    }
  }

  private subscribeToCustomDates(): void {
    this.customDatesSubscription = this.paymentTermsService.$customDates
      .subscribe(() => {
        this.outputPaymentTermForm.emit(new PaymentTermForm(this.paymentTermForm, this.paymentTerm, this.previewData, this.applied));
      });
  }

  private subscribeToUpfrontPaymentAmount(): void {
    this.upfrontPaymentAmountSubscription = this.paymentTermsService.$upfrontPayment
      .subscribe((upfrontAmount: any) => {
        if (typeof upfrontAmount === 'string' && this.initialAmount !== upfrontAmount) {
          this.initialAmount = upfrontAmount;
          if (this.paymentTermForm) { this.loadPreview(Number(this.paymentTermForm.value.paymentTermSelect)); }
        }
      });
  }

  private subscribeToTriggerError(): void {
    this.triggerErrorSubscription = this.paymentTermsService.$triggerError
      .subscribe((triggers: ErrorTriggers) => {
        this.applied = triggers.appliedStatus;
        if (this.paymentTermForm && triggers.triggerError) {
          this.paymentTermSelectControl.markAsDirty();
          this.paymentTermSelectControl.setErrors({ required: true });
          this.paymentTermSelectControl.updateValueAndValidity();
        }
        if (triggers.emitForm) {
          this.outputPaymentTermForm.emit(new PaymentTermForm(this.paymentTermForm, this.paymentTerm, this.previewData, this.applied));
        }
      });
  }

  private setSelectedTerm(paymentTermId: number): void {
    this.paymentTerm = this.paymentTermsList.find(term => term.id === paymentTermId);
    this.outputPaymentTermForm.emit(new PaymentTermForm(this.paymentTermForm, this.paymentTerm, this.previewData));
    this.setShowUpfrontAmountForm();
    this.itemized = this.saleGroupItems && this.saleGroupItems.length > 0;
    this.paymentTermsService.selectedPaymentTermSubject.next({
      paymentTerm: this.paymentTerm,
      formBuilder: this.formBuilder,
      preSelectedCustomDates: this.preSelectedCustomDates ? JSON.parse(this.preSelectedCustomDates) : undefined,
      preSelectedTermId: this.preSelectedTermId
    });
  }

  private setShowUpfrontAmountForm(): void {
    this.showUpfrontAmountForm = (
      this.paymentTerm &&
      this.isInvoice &&
      !this.saleGroupItems &&
      this.paymentTerm.payment_term_strategy.strategy_type !== 'due_in_full'
    );
  }

  private setupForm(): void {
    this.paymentTermForm = this.formBuilder.group({
      paymentTermSelect: ['', Validators.required],
      showPartialAmountInput: [this.showPartialAmountInput]
    });
  }
}
